import React from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Contact from '../components/Contact';
import { cases } from '../data/cases';

// Map service IDs to case tags/categories for matching
const getRelatedCases = (serviceId) => {
  const serviceTagMap = {
    'web-development': ['WEB'],
    'mobile-app-development': ['MOBILE', 'FLUTTER'],
    'e-commerce-solutions': ['E-COMMERCE'],
    'data-science-ai': ['AI'],
    'saas': ['SAAS'],
    'api-integration': ['API'],
    'qa-testing': [],
    'software': [],
  };

  const tags = serviceTagMap[serviceId] || [];
  if (tags.length === 0) return [];

  return cases.filter(c =>
    c.tags.some(tag => tags.includes(tag)) ||
    tags.some(t => c.category.toUpperCase().includes(t))
  );
};

// Map route IDs to translation keys
const serviceIdToKey = {
  'web-development': 'webDevelopment',
  'mobile-app-development': 'mobileApp',
  'e-commerce-solutions': 'ecommerce',
  'data-science-ai': 'dataScience',
  'saas': 'saas',
  'api-integration': 'apiIntegration',
  'qa-testing': 'qaTesting',
  'software': 'customSoftware',
};

const serviceImages = {
  'web-development': '/images/services/Webdev.webp',
  'mobile-app-development': '/images/services/AppDev.webp',
  'e-commerce-solutions': '/images/services/ecommerce.webp',
  'data-science-ai': '/images/services/DataScienceandAi.webp',
  'saas': '/images/services/SaaS.webp',
  'api-integration': '/images/services/API_Integrations.webp',
  'qa-testing': '/images/services/QA_Testing.webp',
  'software': '/images/services/CustomDevelopment.webp',
};

const ServiceDetail = () => {
  const { serviceId, lang = 'en' } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('services');

  const serviceKey = serviceIdToKey[serviceId];

  if (!serviceKey) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-32 pb-20 px-4 text-center">
          <h1 className="text-4xl font-bold text-text-primary mb-4">Service Not Found</h1>
          <button
            onClick={() => navigate(`/${lang}/services`)}
            className="px-6 py-3 bg-text-primary text-white rounded-full font-semibold hover:bg-gray-800 transition-all"
          >
            {t('detail.backToServices')}
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const features = t(`items.${serviceKey}.features`, { returnObjects: true }) || [];
  const benefits = t(`items.${serviceKey}.benefits`, { returnObjects: true }) || [];

  return (
    <div className="bg-white">
      <Navbar />

      {/* Hero Section - White Background */}
      <section className="bg-white min-h-screen py-20 flex items-center" data-section="service-intro">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="mb-8">
            <button
              onClick={() => navigate(`/${lang}/services`)}
              className="flex items-center gap-2 text-grey-accent hover:text-text-primary transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {t('detail.backToServices')}
            </button>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex justify-between items-start mb-16"
          >
            <div>
              <p className="text-secondary-accent text-sm font-semibold mb-2 uppercase tracking-wider flex items-center gap-2">
                <span className="w-2 h-2 bg-secondary-accent rounded-full"></span>
                {t('allServices.badge')}
              </p>
              <h1 className="text-4xl lg:text-5xl font-bold text-text-primary mb-4">
                {t(`items.${serviceKey}.title`)}
              </h1>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start"
          >
            <div className="space-y-8">
              <div className="space-y-6">
                <p className="text-xl text-text-primary leading-relaxed">
                  {t(`items.${serviceKey}.description`)}
                </p>
                <p className="text-lg text-grey-accent leading-relaxed">
                  {t(`items.${serviceKey}.detailedDescription`)}
                </p>
              </div>
            </div>

            <div className="relative h-80 bg-[#F9F9F9] rounded-2xl overflow-hidden">
              <img
                src={serviceImages[serviceId]}
                srcSet={serviceImages[serviceId].replace(/\.(png|jpg|jpeg)$/i, '.webp')}
                alt={t(`items.${serviceKey}.title`)}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </motion.div>

          {/* Features & Benefits Section - Side by Side */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-24 mb-12"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              {/* Features Column */}
              <div>
                <div className="mb-8">
                  <p className="text-secondary-accent text-sm font-semibold uppercase tracking-wider flex items-center gap-2 mb-3">
                    <span className="w-2 h-2 bg-secondary-accent rounded-full"></span>
                    {t('detail.keyFeatures')}
                  </p>
                  <h2 className="text-3xl font-bold text-text-primary">{t('detail.keyFeatures')}</h2>
                </div>

                <div className="space-y-6">
                  {Array.isArray(features) && features.map((feature, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.05 }}
                      className="flex items-start gap-4 group"
                    >
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#F9F9F9] flex items-center justify-center mt-1 group-hover:bg-secondary-accent transition-colors duration-300">
                        <svg className="w-4 h-4 text-secondary-accent group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-text-primary mb-1 group-hover:text-secondary-accent transition-colors duration-300">
                          {feature}
                        </h3>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Benefits Column */}
              <div>
                <div className="mb-8">
                  <p className="text-secondary-accent text-sm font-semibold uppercase tracking-wider flex items-center gap-2 mb-3">
                    <span className="w-2 h-2 bg-secondary-accent rounded-full"></span>
                    {t('detail.benefits')}
                  </p>
                  <h2 className="text-3xl font-bold text-text-primary">{t('detail.benefits')}</h2>
                </div>

                <div className="space-y-6">
                  {Array.isArray(benefits) && benefits.map((benefit, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.05 }}
                      className="flex items-start gap-4 group"
                    >
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#F9F9F9] flex items-center justify-center mt-1 group-hover:bg-secondary-accent transition-colors duration-300">
                        <svg className="w-4 h-4 text-secondary-accent group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-text-primary mb-1 group-hover:text-secondary-accent transition-colors duration-300">
                          {benefit}
                        </h3>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related Projects Section */}
      {(() => {
        const relatedCases = getRelatedCases(serviceId);
        if (relatedCases.length === 0) return null;
        return (
          <section className="bg-black py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="mb-12"
              >
                <p className="text-white/60 text-sm font-semibold mb-2 uppercase tracking-wider flex items-center gap-2">
                  <span className="w-2 h-2 bg-white/60 rounded-full"></span>
                  {t('allCases.badge')}
                </p>
                <h2 className="text-3xl lg:text-4xl font-bold text-white">
                  {t(`items.${serviceKey}.title`)}
                </h2>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedCases.map((caseItem, index) => (
                  <motion.div
                    key={caseItem.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="group cursor-pointer"
                    onClick={() => navigate(`/${lang}/cases/${caseItem.id}`)}
                  >
                    <div className="bg-[#111111] rounded-2xl overflow-hidden group-hover:bg-[#1a1a1a] transition-all duration-300 border border-white/5 hover:border-white/10">
                      {/* Image */}
                      <div className="relative aspect-[4/3] overflow-hidden">
                        <img
                          src={caseItem.screenshot}
                          alt={`${caseItem.title} screenshot`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        {/* Arrow */}
                        <div className="absolute top-4 right-4">
                          <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:bg-white/20 transition-all duration-300">
                            <svg className="w-5 h-5 text-white transform -rotate-45" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                          </div>
                        </div>
                      </div>
                      {/* Content */}
                      <div className="p-6">
                        <p className="text-white/50 text-xs font-bold mb-2 uppercase tracking-widest">
                          {caseItem.category}
                        </p>
                        <h3 className="text-xl font-bold text-white mb-2">
                          {caseItem.title}
                        </h3>
                        <p className="text-white/60 text-sm leading-relaxed line-clamp-2">
                          {caseItem.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        );
      })()}

      {/* Contact Section */}
      <Contact />

      <Footer />
    </div>
  );
};

export default ServiceDetail;